export interface SucursalDescuento {
  id: string;
  nombre: string;
  incluida: boolean;
}

export interface CategoriaDescuento {
  id: string;
  nombre: string;
  padre?: string;
  cantidadProductos: number;
}

export interface Descuento {
  id: string;
  nombre: string;
  porcentaje: number;
  montoMaximo: number;
  activo: boolean;
  categoriasExcluidas: CategoriaDescuento[];
  sucursales: SucursalDescuento[];
}

export interface DescuentoFormValue {
  nombre: string;
  porcentaje: number;
  montoMaximo: number;
  activo: boolean;
  categoriasExcluidas: CategoriaDescuento[];
  sucursales: SucursalDescuento[];
}

export function nombreCompletoCategoria(categoria: CategoriaDescuento): string {
  return categoria.padre ? `${categoria.padre} > ${categoria.nombre}` : categoria.nombre;
}

export type TipoDescuentoCupon = 'monto' | 'porcentaje';

export type ModoRegistroCupon = 'codigo' | 'lotes';

export type TipoConsumoCategoria = 'unico' | 'multiple';

export interface CategoriaCupon {
  id: string;
  descripcion: string;
  tipoConsumo: TipoConsumoCategoria;
  fechaExpiracionDefecto: string;
  activa: boolean;
}

export interface ProductoCupon {
  id: string;
  nombre: string;
  presentacion: string;
}

export interface LoteCupon {
  sigla: string;
  numeroInicial: string;
  numeroFinal: string;
}

export interface CuponDescuento {
  id: string;
  codigo: string;
  cantidadLote?: number;
  modoRegistro: ModoRegistroCupon;
  lote?: LoteCupon;
  categoriaId: string;
  categoriaNombre: string;
  tipoDescuento: TipoDescuentoCupon;
  valorDescuento: number;
  montoMaximo: number;
  montoDisponible: number;
  montoUsado: number;
  fechaExpiracion: string;
  activo: boolean;
  sucursales: SucursalDescuento[];
  productos: ProductoCupon[];
}

export interface CuponFormValue {
  categoriaId: string;
  fechaExpiracion: string;
  tipoDescuento: TipoDescuentoCupon;
  valorDescuento: number;
  montoMaximo: number;
  activo: boolean;
  modoRegistro: ModoRegistroCupon;
  codigo: string;
  lote: LoteCupon;
  sucursales: SucursalDescuento[];
  productos: ProductoCupon[];
}

export interface CategoriaCuponFormValue {
  descripcion: string;
  tipoConsumo: TipoConsumoCategoria;
  fechaExpiracionDefecto: string;
  activa: boolean;
}

export function etiquetaTipoConsumo(tipo: TipoConsumoCategoria): string {
  return tipo === 'unico'
    ? 'Valido para un solo consumo'
    : 'Valido para un múltiples consumos';
}

export function codigoLoteCupon(cupon: CuponDescuento): string {
  return cupon.cantidadLote ? `${cupon.codigo} - ${cupon.cantidadLote}` : cupon.codigo;
}

export function cuponExpirado(cupon: CuponDescuento, hoy: Date = new Date()): boolean {
  const expiracion = new Date(`${cupon.fechaExpiracion}T23:59:59`);
  return expiracion.getTime() < hoy.getTime();
}

export type TipoOferta = 'dos-en-uno' | 'dos-por-uno';

export type TipoPeriodoOferta = 'determinado' | 'permanente';

export type DiaSemana = 'L' | 'M' | 'X' | 'J' | 'V' | 'S' | 'D';

export const DIAS_SEMANA: { valor: DiaSemana; etiqueta: string }[] = [
  { valor: 'L', etiqueta: 'L' },
  { valor: 'M', etiqueta: 'M' },
  { valor: 'X', etiqueta: 'M' },
  { valor: 'J', etiqueta: 'J' },
  { valor: 'V', etiqueta: 'V' },
  { valor: 'S', etiqueta: 'S' },
  { valor: 'D', etiqueta: 'D' },
];

export interface PeriodoOferta {
  tipo: TipoPeriodoOferta;
  inicia: string;
  finaliza: string;
  horaInicio: string;
  horaFin: string;
  dias: DiaSemana[];
}

export interface ProductoOferta {
  id: string;
  nombre: string;
  presentacion: string;
  compra: number;
  valor: number;
}

export interface SucursalOferta {
  id: string;
  nombre: string;
  incluida: boolean;
  precioPropio: boolean;
  precio: number;
}

export interface Oferta {
  id: string;
  tipo: TipoOferta;
  producto: ProductoOferta;
  periodo: PeriodoOferta;
  cantidadMaxima: number;
  stock: number;
  sucursales: SucursalOferta[];
}

export interface OfertaFormValue {
  periodo: PeriodoOferta;
  cantidadMaxima: number;
  stock: number;
  productos: ProductoOferta[];
  sucursales: SucursalOferta[];
}

export interface OfertaEdicionRapida {
  compra: number;
  valor: number;
  cantidadMaxima: number;
  stock: number;
}

export function periodoOfertaVacio(): PeriodoOferta {
  return {
    tipo: 'determinado',
    inicia: '',
    finaliza: '',
    horaInicio: '',
    horaFin: '',
    dias: [],
  };
}

export function ofertaVigente(
  oferta: { periodo: PeriodoOferta },
  ahora: Date = new Date(),
): boolean {
  const { periodo } = oferta;

  if (periodo.tipo === 'permanente') {
    return periodo.dias.length > 0;
  }

  if (!periodo.inicia || !periodo.finaliza) return false;

  const inicio = new Date(periodo.inicia).getTime();
  const fin = new Date(periodo.finaliza).getTime();
  const t = ahora.getTime();

  return Number.isFinite(inicio) && Number.isFinite(fin) && t >= inicio && t <= fin;
}

export interface ConfigOferta {
  tipo: TipoOferta;
  ruta: string;
  titulo: string;
  tituloRegistro: string;
  etiquetaValor: string;
  valorMonetario: boolean;
  textoCrear: string;
  descripcionVacio: string;
}

export const CONFIG_OFERTAS: Record<TipoOferta, ConfigOferta> = {
  'dos-en-uno': {
    tipo: 'dos-en-uno',
    ruta: 'dos-en-uno',
    titulo: '2 en 1',
    tituloRegistro: 'Registrar oferta 2 en 1',
    etiquetaValor: 'Al precio de',
    valorMonetario: true,
    textoCrear: 'Nueva oferta',
    descripcionVacio: 'Da clic en “Nueva oferta” para crear una oferta 2 en 1',
  },
  'dos-por-uno': {
    tipo: 'dos-por-uno',
    ruta: 'dos-por-uno',
    titulo: '2 × 1',
    tituloRegistro: 'Registrar oferta 2 × 1',
    etiquetaValor: 'Lleva',
    valorMonetario: false,
    textoCrear: 'Crear oferta',
    descripcionVacio: 'Da clic en “Crear oferta” para crear una oferta 2 por 1',
  },
};

export interface ProductoPrecioBajo {
  id: string;
  nombre: string;
  presentacion: string;
  precio: number;
  cantidadMinima: number;
  cantidadMaxima: number;
}

export interface OfertaPrecioBajo {
  id: string;
  producto: ProductoPrecioBajo;
  periodo: PeriodoOferta;
  menuOnline: boolean;
  porRango: boolean;
  stock: number;
  version: string;
  sucursales: SucursalOferta[];
}

export interface PrecioBajoFormValue {
  periodo: PeriodoOferta;
  menuOnline: boolean;
  porRango: boolean;
  productos: ProductoPrecioBajo[];
  sucursales: SucursalOferta[];
}

export interface PrecioBajoEdicionRapida {
  precio: number;
  cantidadMinima: number;
  cantidadMaxima: number;
  stock: number;
}

export interface SeleccionPrecioBajo {
  porRango: boolean;
  productos: ProductoPrecioBajo[];
}
